import React, { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { Button, Tag } from "antd";
import { CheckCircleOutlined, DeploymentUnitOutlined, RightOutlined } from "@ant-design/icons";
import styled from "styled-components";

import { useApi, ApiNames } from "../../hooks/api";
import { useGetSetting } from "../../hooks/getSetting";
import { selectCanAccessAgenticPlatform } from "../../store/selector";
import { Routes } from "../../constant/routes";
import { HITL_TASK_TYPES } from "../../constant/agenticPlatform";
import { GRAY_200, GRAY_500, PRIMARY_50, PRIMARY_600, SUCCESS_500, WHITE } from "../../constant/colors";
import { AgenticStatsHeader } from "./AgenticStatsHeader";
import { ActionButton, AgenticPanelIcon, AgenticPanelTitle } from "./taskStyles";
import { useAutoRefetch } from "./useAutoRefetch";
import { patientLabel, formatRelativeTime, hitlContextLabel } from "./taskHelpers";

/** How many of the oldest pending tasks the home page surfaces. */
const TOP_TASKS_LIMIT = 3;

const SummaryContainer = styled.div`
  padding: 0 30px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SummaryHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TaskRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  background: ${WHITE};
  border: 1px solid ${GRAY_200};
  border-radius: 10px;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
  &:hover {
    box-shadow: 0 2px 8px rgba(16, 24, 40, 0.08);
  }
`;

// Initials disc, matching the section icon's soft-tint treatment — gives each
// row a visual anchor so the list doesn't read as bare text lines.
const TaskAvatar = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${PRIMARY_50};
  color: ${PRIMARY_600};
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

// Two-line identity cluster: the patient on top, what the task IS below it —
// the row's whole story reads top-to-bottom on the left, leaving the right
// edge to "how long" + the action.
const TaskMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
  a {
    color: ${PRIMARY_600};
    font-weight: 600;
    font-size: 14px;
    line-height: 1.2;
    width: fit-content;
  }
`;

const TaskContext = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${GRAY_500};
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TaskPending = styled.div`
  color: ${GRAY_500};
  font-size: 12px;
  white-space: nowrap;
`;

/** "BMS Demo" → "BD"; single word → its first two letters; no name → "#". */
const patientInitials = (task) => {
  const name = (task?.patientName || "").trim();
  if (!name) return "#";
  const words = name.split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

const EmptyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  border: 1px dashed ${GRAY_200};
  border-radius: 10px;
  color: ${GRAY_500};
  font-size: 13px;
`;

/**
 * Home-page agentic summary: the general-statistics header plus the few OLDEST
 * pending navigator tasks (consent decisions + enrollment reviews), each
 * deep-linking into the Tasks hub, with a "view all" link for the rest. When
 * nothing is pending, a quiet all-caught-up line keeps the section honest
 * rather than silently shrinking.
 *
 * Gated exactly like every agentic surface — the per-site
 * `tmAgenticPlatformEnabled` setting AND the user gate — so the home page of a
 * non-agentic site (or user) is untouched. Renders nothing while hidden.
 *
 * Deliberately consent + EE only: those are the two NAVIGATOR queues. Manual
 * EMR fills and screenshot uploads are operator work with their own Tasks-hub
 * sections; surfacing them on every navigator's home page would just be noise.
 */
const HomeAgenticSummary = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [tmAgenticPlatformEnabled] = useGetSetting(["tmAgenticPlatformEnabled"]);
  const canAccessAgenticPlatform = useSelector(selectCanAccessAgenticPlatform);

  const [fetchConsent, consentResult] = useApi(ApiNames.AgenticHitlTasks);
  const [fetchEeReview, eeReviewResult] = useApi(ApiNames.AgenticHitlTasks);

  // Same identity dance as the other agentic surfaces: `useApi` hands back a
  // fresh invoke per render; the auto-refetch runs off refs + one stable callback.
  const fetchConsentRef = useRef(fetchConsent);
  fetchConsentRef.current = fetchConsent;
  const fetchEeReviewRef = useRef(fetchEeReview);
  fetchEeReviewRef.current = fetchEeReview;
  const enabled = Boolean(tmAgenticPlatformEnabled && canAccessAgenticPlatform);
  const refetch = useCallback(() => {
    if (!enabled) return;
    // Oldest-first server-side; TOP_TASKS_LIMIT from each queue is plenty to
    // pick the overall oldest TOP_TASKS_LIMIT after the merge.
    fetchConsentRef.current({ hitlTaskType: HITL_TASK_TYPES.CONSENT_DECIDE, limit: TOP_TASKS_LIMIT, offset: 0 });
    fetchEeReviewRef.current({ hitlTaskType: HITL_TASK_TYPES.EE_REVIEW, limit: TOP_TASKS_LIMIT, offset: 0 });
  }, [enabled]);
  useAutoRefetch(refetch);

  const topTasks = useMemo(() => {
    const consentItems = (consentResult?.items ?? []).map((item) => ({
      ...item,
      hitlTaskType: HITL_TASK_TYPES.CONSENT_DECIDE
    }));
    const eeItems = (eeReviewResult?.items ?? []).map((item) => ({
      ...item,
      hitlTaskType: HITL_TASK_TYPES.EE_REVIEW
    }));
    return [...consentItems, ...eeItems]
      .sort((a, b) => new Date(a.awaitingSince) - new Date(b.awaitingSince))
      .slice(0, TOP_TASKS_LIMIT);
  }, [consentResult, eeReviewResult]);

  const totalPending = (consentResult?.total ?? 0) + (eeReviewResult?.total ?? 0);
  // Distinguish "no work" from "not loaded yet" — the all-caught-up line only
  // renders once both feeds have actually answered.
  const loaded = Boolean(consentResult && eeReviewResult);

  if (!enabled) return null;

  const openInTasksHub = (agenticRunId) =>
    history.push(`${Routes.AGENTIC_PLATFORM_TASKS}?agenticRunId=${agenticRunId}`);

  return (
    <SummaryContainer data-testid="home-agentic-summary">
      <SummaryHeaderRow>
        <AgenticPanelTitle>
          <AgenticPanelIcon>
            <DeploymentUnitOutlined />
          </AgenticPanelIcon>
          {t("agentic_stats.home_title")}
        </AgenticPanelTitle>
        <Button
          type="link"
          onClick={() => history.push(Routes.AGENTIC_PLATFORM_TASKS)}
          data-testid="home-agentic-view-all"
        >
          {totalPending > 0
            ? t("agentic_stats.view_all_with_count", { count: totalPending })
            : t("agentic_stats.view_all")}
          <RightOutlined style={{ fontSize: 11 }} />
        </Button>
      </SummaryHeaderRow>

      <AgenticStatsHeader />

      {topTasks.map((task) => (
        <TaskRow key={task.agenticRunId} data-testid={`home-agentic-task-${task.agenticRunId}`}>
          <TaskAvatar aria-hidden>{patientInitials(task)}</TaskAvatar>
          <TaskMain>
            <Link
              to={Routes.PATIENT_PATH({ patientId: task.patientId })}
              target="_blank"
              rel="noreferrer"
              data-testid={`home-agentic-patient-${task.agenticRunId}`}
            >
              {patientLabel(task)}
            </Link>
            <TaskContext>
              {task.hitlTaskType === HITL_TASK_TYPES.EE_REVIEW ? (
                <Tag color="purple" style={{ marginRight: 0, lineHeight: "18px" }}>
                  {t("agentic_stats.task_kind_ee_review")}
                </Tag>
              ) : (
                <Tag color="blue" style={{ marginRight: 0, lineHeight: "18px" }}>
                  {t("agentic_stats.task_kind_consent")}
                </Tag>
              )}
              <span>{hitlContextLabel(task, t)}</span>
            </TaskContext>
          </TaskMain>
          <TaskPending>{formatRelativeTime(task.awaitingSince)}</TaskPending>
          <ActionButton
            size="small"
            data-testid={`home-agentic-review-${task.agenticRunId}`}
            onClick={() => openInTasksHub(task.agenticRunId)}
          >
            {t("agentic_stats.review")}
          </ActionButton>
        </TaskRow>
      ))}

      {loaded && topTasks.length === 0 && (
        <EmptyRow data-testid="home-agentic-empty">
          <CheckCircleOutlined style={{ color: SUCCESS_500 }} />
          {t("agentic_stats.empty_tasks")}
        </EmptyRow>
      )}
    </SummaryContainer>
  );
};

export { HomeAgenticSummary };
